import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { talmidim as seedTalmidim, klasn as seedKlasn } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TalmidimContext = createContext(null);

export function TalmidimProvider({ children }) {
  const [talmidim, setTalmidim] = useState([]);
  const [klasn, setKlasn] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [tRes, kRes] = await Promise.all([
        axios.get(`${API}/talmidim`),
        axios.get(`${API}/klasn`),
      ]);
      setTalmidim(tRes.data || []);
      setKlasn(kRes.data || []);
      return { talmidim: tRes.data, klasn: kRes.data };
    } catch (e) {
      console.error("Failed to load talmidim/klasn", e);
      return { talmidim: [], klasn: [] };
    }
  }, []);

  // On first mount: fetch, and if empty seed from mock (one-time)
  useEffect(() => {
    (async () => {
      const { talmidim: t } = await refresh();
      if ((t?.length || 0) === 0 && !localStorage.getItem("oht_talmidim_seeded")) {
        try {
          // Seed klasn first (also happens implicitly during bulk-import)
          const items = seedTalmidim.slice(0, 60).map((s) => ({
            name: s.fullName,
            klas: s.klasName,
            father: s.fatherName,
            phone: s.parentPhone || "",
          }));
          await axios.post(`${API}/talmidim/bulk-import`, { items, replace: false });
          localStorage.setItem("oht_talmidim_seeded", "1");
          await refresh();
        } catch (e) {
          console.error("Seeding talmidim failed", e);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const bulkImport = useCallback(async (items, replace = false) => {
    const res = await axios.post(`${API}/talmidim/bulk-import`, { items, replace });
    await refresh();
    return res.data;
  }, [refresh]);

  const addTalmid = useCallback(async ({ fullName, klasName, father = "", phone = "" }) => {
    const res = await axios.post(`${API}/talmidim`, {
      fullName: fullName.trim(),
      klasName: klasName.trim(),
      fatherName: father.trim(),
      phone: phone.trim(),
    });
    await refresh();
    return res.data;
  }, [refresh]);

  const deleteTalmid = useCallback(async (id) => {
    await axios.delete(`${API}/talmidim/${id}`);
    setTalmidim((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <TalmidimContext.Provider value={{ talmidim, klasn, loading, refresh, bulkImport, addTalmid, deleteTalmid }}>
      {children}
    </TalmidimContext.Provider>
  );
}

export function useTalmidim() {
  const ctx = useContext(TalmidimContext);
  if (!ctx) throw new Error("useTalmidim must be used within TalmidimProvider");
  return ctx;
}
